import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Input, Row, Space, Typography, message } from "antd";
import api from "../../api/axios";

const { Title, Text } = Typography;

type ResellerProfileForm = {
  businessName?: string;
  registrationNumber?: string;
  vatNumber?: string;
  country?: string;
  city?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
};

const pickValue = (data: any, keys: string[]) => {
  for (const key of keys) {
    if (data && data[key] !== undefined && data[key] !== null) {
      return data[key];
    }
  }
  return "";
};

const toFormValue = (value: any) => (value == null ? "" : String(value));

const toPayloadValue = (value?: string) => {
  const trimmed = (value || "").trim();
  return trimmed ? trimmed : null;
};

const mapProfileToForm = (data: any): ResellerProfileForm => ({
  businessName: toFormValue(
    pickValue(data, ["businessName", "BusinessName", "business_name"])
  ),
  registrationNumber: toFormValue(
    pickValue(data, ["registrationNumber", "RegistrationNumber", "registration_number"])
  ),
  vatNumber: toFormValue(pickValue(data, ["vatNumber", "VatNumber", "vat_number"])),
  country: toFormValue(pickValue(data, ["country", "Country"])),
  city: toFormValue(pickValue(data, ["city", "City"])),
  address: toFormValue(pickValue(data, ["address", "Address"])),
  phone: toFormValue(pickValue(data, ["phone", "Phone"])),
  email: toFormValue(pickValue(data, ["email", "Email"])),
  website: toFormValue(pickValue(data, ["website", "Website"])),
});

export default function ResellerBusinessInfo() {
  const [form] = Form.useForm<ResellerProfileForm>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get("/pos/Resellers/profile");
      const data = res?.data && typeof res.data === "object" ? res.data : {};
      form.setFieldsValue(mapProfileToForm(data));
    } catch (error: any) {
      message.error(error?.displayMessage || "Failed to load business info");
      form.resetFields();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (values: ResellerProfileForm) => {
    setSaving(true);
    try {
      const payload = {
        businessName: toPayloadValue(values.businessName),
        registrationNumber: toPayloadValue(values.registrationNumber),
        vatNumber: toPayloadValue(values.vatNumber),
        country: toPayloadValue(values.country),
        city: toPayloadValue(values.city),
        address: toPayloadValue(values.address),
        phone: toPayloadValue(values.phone),
        email: toPayloadValue(values.email),
        website: toPayloadValue(values.website),
      };

      await api.put("/pos/Resellers/profile", payload);
      message.success("Business info saved");
    } catch (error: any) {
      message.error(error?.displayMessage || "Failed to save business info");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 980 }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>
            Business Info
          </Title>
          <Text type="secondary">
            Update your company details used for invoicing and compliance.
          </Text>
        </div>

        <Card loading={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            disabled={loading || saving}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item name="businessName" label="Business Name">
                  <Input placeholder="Company name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="registrationNumber" label="Registration Number">
                  <Input placeholder="Registration number" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="vatNumber" label="VAT Number">
                  <Input placeholder="VAT number" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="phone" label="Phone">
                  <Input placeholder="Phone number" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="email"
                  label="Business Email"
                  rules={[{ type: "email", message: "Enter a valid email" }]}
                >
                  <Input placeholder="email@company.com" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="website" label="Website">
                  <Input placeholder="https://example.com" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="country" label="Country">
                  <Input placeholder="Country" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="city" label="City">
                  <Input placeholder="City" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item name="address" label="Address">
                  <Input.TextArea rows={3} placeholder="Street, number, zip" />
                </Form.Item>
              </Col>
            </Row>

            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button type="primary" htmlType="submit" loading={saving}>
                Save
              </Button>
            </Space>
          </Form>
        </Card>
      </Space>
    </div>
  );
}
